<template>
  <div class="financial__grid-shell">
    <div class="financial__grid-toolbar">
      <div class="financial__segmented" role="group" aria-label="Show rows">
        <button
          v-for="option in filterOptions"
          :key="option.value"
          type="button"
          :aria-pressed="filter === option.value"
          @click="setFilter(option.value)"
        >
          {{ option.label }} <span class="financial__grid-count">{{ numberLabel(counts[option.value]) }}</span>
        </button>
      </div>
      <div class="financial__grid-search">
        <label class="visually-hidden" for="financial-grid-search">Find in the grid</label>
        <input
          id="financial-grid-search"
          v-model="search"
          class="form-control"
          type="search"
          placeholder="Find shipment, charge or note"
          autocomplete="off"
        />
      </div>
      <div class="financial__grid-history">
        <button class="financial__button financial__button--small" type="button" :disabled="!canUndo" @click="undo">
          <mdicon name="undo" :size="16" aria-hidden="true" /> Undo
        </button>
        <button class="financial__button financial__button--small" type="button" :disabled="!canRedo" @click="redo">
          <mdicon name="redo" :size="16" aria-hidden="true" /> Redo
        </button>
      </div>
      <div class="financial__grid-end">
        <p class="financial__grid-save" :class="{ 'financial__grid-save--error': save === 'error' }" role="status">
          <mdicon :name="save === 'error' ? 'alert-outline' : save === 'saving' ? 'sync' : 'cloud-check-outline'" :size="16" aria-hidden="true" />
          {{ saveText }}
          <button v-if="save === 'error'" class="financial__link" type="button" @click="retry">Retry saving</button>
        </p>
        <!-- The rules and keys, folded: daily users know them, and the grid stays in view. -->
        <details ref="help" class="financial__grid-help" @keydown.esc="closeHelp" @focusout="leaveHelp">
          <summary>Shortcuts</summary>
          <div class="financial__grid-help-panel" tabindex="-1">
            <ul>
              <li>To leave a row out, untick Bill and add a note with the reason.</li>
              <li>A changed row counts as checked.</li>
              <li>Changes save as you type and apply when you approve.</li>
            </ul>
            <dl>
              <template v-for="shortcut in shortcuts" :key="shortcut.action">
                <dt>
                  <template v-for="(key, index) in shortcut.keys" :key="key">{{ index ? ' or ' : '' }}<kbd>{{ key }}</kbd></template>
                </dt>
                <dd>{{ shortcut.action }}</dd>
              </template>
            </dl>
          </div>
        </details>
      </div>
    </div>

    <div class="financial__grid-cellbar">
      <span class="financial__grid-ref">{{ activeRef }}</span>
      <span class="financial__grid-value" :class="{ financial__attention: !!editError }">{{ editError || activeValue }}</span>
    </div>

    <div
      ref="scroller"
      class="financial__grid"
      role="grid"
      tabindex="0"
      aria-label="Rows to review. Customer price, Bill and Note can be edited like a spreadsheet."
      aria-multiselectable="true"
      :aria-rowcount="shown.length + 1"
      :aria-colcount="columns.length"
      :aria-activedescendant="shown.length ? cellId(active.r, active.c) : undefined"
      @keydown="onKey"
      @copy="onCopy"
      @paste="onPaste"
      @scroll="onScroll"
    >
      <div class="financial__grid-inner" :style="{ width: `${totalWidth}px`, height: `${(shown.length + 1) * rowHeight}px` }">
        <div class="financial__grid-row financial__grid-head" role="row" aria-rowindex="1" :style="{ gridTemplateColumns: template }">
          <div
            v-for="(column, c) in columns"
            :key="column.key"
            role="columnheader"
            class="financial__grid-cell"
            :class="{ 'financial__grid-cell--numeric': column.numeric, 'financial__grid-cell--field': column.field }"
            :aria-colindex="c + 1"
          >
            {{ column.label }}<mdicon v-if="column.field" name="pencil-outline" :size="12" aria-hidden="true" />
          </div>
        </div>
        <div
          v-for="item in windowRows"
          :key="item.row.key"
          role="row"
          class="financial__grid-row"
          :class="[`financial__grid-row--${item.row.tone}`, { 'financial__grid-row--edited': item.row.edited }]"
          :aria-rowindex="item.index + 2"
          :style="{ gridTemplateColumns: template, transform: `translateY(${(item.index + 1) * rowHeight}px)` }"
        >
          <div
            v-for="(column, c) in columns"
            :id="cellId(item.index, c)"
            :key="column.key"
            role="gridcell"
            class="financial__grid-cell"
            :class="cellClass(item.index, c, item.row)"
            :aria-colindex="c + 1"
            :aria-selected="inSelection(item.index, c)"
            :aria-readonly="!editableAt(item.row, c)"
            @mousedown="onDown(item.index, c, $event)"
            @mouseenter="onEnter(item.index, c)"
            @click="onClick(item.index, c, $event)"
            @dblclick="onDouble(c)"
          >
            <input
              v-if="editing && item.row.key === editKey && c === editColumn"
              ref="editor"
              v-model="draft"
              class="financial__grid-editor"
              :class="{ 'financial__grid-editor--numeric': column.numeric }"
              :aria-label="`${column.label} for ${item.row.shipment}`"
              :aria-invalid="!!editError"
              autocomplete="off"
              @keydown="onEditorKey"
              @blur="onEditorBlur"
              @mousedown.stop
              @click.stop
              @dblclick.stop
            />
            <span
              v-else-if="column.key === 'bill'"
              class="financial__grid-check"
              :class="{ 'financial__grid-check--on': item.row.bill }"
              aria-hidden="true"
            >
              <mdicon :name="item.row.bill ? 'checkbox-marked' : 'checkbox-blank-outline'" :size="18" />
            </span>
            <template v-else-if="column.key === 'shipment'">
              <span class="financial__grid-text">{{ item.row.shipment }}</span>
              <!-- For the mouse; keyboard users press Enter on the cell. -->
              <button
                v-if="item.row.itemId"
                class="financial__grid-open"
                type="button"
                tabindex="-1"
                :aria-label="`Open shipment ${item.row.shipment}`"
                @mousedown.stop
                @dblclick.stop
                @click="openShipment(item.row.itemId)"
              >
                <mdicon name="chevron-right" :size="16" aria-hidden="true" />
              </button>
            </template>
            <!-- A row to check, or changed after billing, is marked with an icon as well as its colour and status text. -->
            <template v-else-if="column.key === 'status' && (item.row.tone === 'check' || item.row.tone === 'changed')">
              <mdicon :name="item.row.tone === 'changed' ? 'swap-horizontal' : 'alert-outline'" :size="14" aria-hidden="true" />
              <span class="financial__grid-text">{{ display(item.row, column.key) }}</span>
            </template>
            <template v-else>{{ display(item.row, column.key) }}</template>
            <span v-if="column.key === 'bill'" class="visually-hidden">{{ item.row.bill ? 'Included' : 'Left out' }}</span>
          </div>
        </div>
        <p v-if="!shown.length" class="financial__grid-empty">No rows match. Choose another filter or clear the search.</p>
      </div>
    </div>

    <div class="financial__grid-status">
      <span>{{ numberLabel(shown.length) }} of {{ numberLabel(rows.length) }} rows</span>
      <span v-if="selectionSummary">{{ selectionSummary }}</span>
    </div>
    <p class="visually-hidden" role="status" aria-live="polite">{{ announcement }}</p>
  </div>
</template>
<script lang="ts">
import type {
  FinancialGridCell,
  FinancialGridColumn,
  FinancialGridColumnKey,
  FinancialGridFilter,
  FinancialGridShortcut,
} from '@/types/Components/FinancialGrid'
import type { FinancialReviewGridCounts, FinancialReviewGridSelection, FinancialReviewGridWindowRows } from '@/types/Components/FinancialViewState'
import type { FinancialGridEdit, FinancialGridField, FinancialGridRow } from '@/types/Models/Financial'
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type ComputedRef,
  type PropType,
  type Ref,
} from 'vue'
import { financialAmountLabel, financialAmountsEqual, financialDecimal, sumFinancialAmounts } from '@/services/FinancialAmounts'

// A review grid modelled on spreadsheet editing: one active cell, range selection, type to edit, copy and paste of
// tab-separated cells (from Google Sheets or Excel), fill down, undo, and changes saved as they are made.
// The cursor follows the row, not its position: rows can leave a filter while you work, as their status changes.
export default defineComponent({
  name: 'FinancialReviewGrid',
  props: {
    rows: { type: Array as PropType<FinancialGridRow[]>, required: true },
    currency: { type: String, default: 'SEK' },
    save: { type: String as PropType<'idle' | 'saving' | 'saved' | 'error'>, default: 'idle' },
    canUndo: { type: Boolean, default: false },
    canRedo: { type: Boolean, default: false },
    // Which rows are shown; the review sets it, for example to the rows that need a check.
    filter: { type: String as PropType<FinancialGridFilter>, default: 'all' },
  },
  emits: ['edit', 'undo', 'redo', 'retry', 'open-shipment', 'update:filter'],
  setup(props, { emit }) {
    const rowHeight = 40
    // The three editable columns sit together right after the cost, so they are in view without scrolling sideways
    // and a three-column block from Google Sheets pastes into Customer price, Bill and Note at once.
    const columns: FinancialGridColumn[] = [
      { key: 'shipment', label: 'Shipment', width: 168 },
      { key: 'status', label: 'Status', width: 200 },
      { key: 'charge', label: 'Charge', width: 150 },
      { key: 'cost', label: `Actual carrier cost${props.currency ? ` (${props.currency})` : ''}`, width: 180, numeric: true },
      { key: 'quotedPrice', label: 'Quoted shipment price (SEK)', width: 208, numeric: true },
      { key: 'price', label: `Customer price${props.currency ? ` (${props.currency})` : ''}`, width: 172, numeric: true, field: 'price' },
      { key: 'bill', label: 'Bill', width: 56, field: 'bill' },
      { key: 'note', label: 'Note', width: 208, field: 'note' },
      { key: 'margin', label: 'Margin', width: 104, numeric: true },
      { key: 'marginPercent', label: '% on cost', width: 84, numeric: true },
      { key: 'basis', label: 'Price basis', width: 180 },
      { key: 'booked', label: 'Booked', width: 104 },
      { key: 'service', label: 'Service', width: 96 },
      { key: 'grams', label: 'Weight (g)', width: 96, numeric: true },
    ]
    const amountKeys: FinancialGridColumnKey[] = ['cost', 'price', 'margin']
    const template = columns.map((column) => `${column.width}px`).join(' ')
    const totalWidth = columns.reduce((total, column) => total + column.width, 0)
    const shortcuts: FinancialGridShortcut[] = [
      { keys: ['Enter', 'F2'], action: 'Edit the cell, or just start typing' },
      { keys: ['Esc'], action: 'Cancel the edit' },
      { keys: ['Tab', 'Arrows'], action: 'Move' },
      { keys: ['Shift+Arrows'], action: 'Select a range, or drag with the mouse' },
      { keys: ['Space'], action: 'Tick or untick Bill, or click it' },
      { keys: ['Ctrl/⌘+C', 'Ctrl/⌘+V'], action: 'Copy and paste, also to and from Google Sheets or Excel' },
      { keys: ['Ctrl/⌘+D'], action: 'Fill down' },
      { keys: ['Delete'], action: 'Reset to the PacSort price, billed and without a note' },
      { keys: ['Ctrl/⌘+Z'], action: 'Undo' },
      { keys: ['Ctrl/⌘+Shift+Z', 'Ctrl/⌘+Y'], action: 'Redo' },
      { keys: ['Ctrl/⌘+Enter'], action: 'Open the shipment' },
    ]
    const scroller: Ref<HTMLElement | null> = ref(null)
    const editor: Ref<HTMLInputElement[] | HTMLInputElement | null> = ref(null)
    const help: Ref<HTMLDetailsElement | null> = ref(null)
    const search: Ref<string> = ref('')
    const active: Ref<FinancialGridCell> = ref({ r: 0, c: 5 })
    const anchor: Ref<FinancialGridCell> = ref({ r: 0, c: 5 })
    const activeKey: Ref<string> = ref('')
    const anchorKey: Ref<string> = ref('')
    const allSelected: Ref<boolean> = ref(false)
    const editing: Ref<boolean> = ref(false)
    const editKey: Ref<string> = ref('')
    const editColumn: Ref<number> = ref(0)
    const editMode: Ref<'enter' | 'edit'> = ref('edit')
    const draft: Ref<string> = ref('')
    const openedWith: Ref<string> = ref('')
    const editError: Ref<string> = ref('')
    const scrollTop: Ref<number> = ref(0)
    const viewport: Ref<number> = ref(480)
    const announcement: Ref<string> = ref('')
    let dragging = false
    let resize: ResizeObserver | null = null

    const matches = (row: FinancialGridRow): boolean => {
      if (props.filter === 'check' && row.tone !== 'check') return false
      if (props.filter === 'edited' && !row.edited) return false
      if (props.filter === 'fees' && row.role === 'base') return false
      const text = search.value.trim().toLowerCase()
      return !text || [row.shipment, row.charge, row.note, row.status, row.reason].join(' ').toLowerCase().includes(text)
    }
    // The row being worked on stays in view until you move on, also when its new status no longer matches the filter.
    const shown: ComputedRef<FinancialGridRow[]> = computed(() =>
      props.rows.filter((row) => matches(row) || row.key === activeKey.value || (editing.value && row.key === editKey.value)),
    )
    const counts: ComputedRef<FinancialReviewGridCounts> = computed(() => ({
      all: props.rows.length,
      check: props.rows.filter((row) => row.tone === 'check').length,
      edited: props.rows.filter((row) => row.edited).length,
      fees: props.rows.filter((row) => row.role !== 'base').length,
    }))
    // Fees and credits only when the customer has any.
    const filterOptions: ComputedRef<{ value: FinancialGridFilter; label: string }[]> = computed(() =>
      [
        { value: 'all' as const, label: 'All rows' },
        { value: 'check' as const, label: 'Needs a check' },
        { value: 'edited' as const, label: 'Edited' },
        { value: 'fees' as const, label: 'Fees and credits' },
      ].filter((option) => option.value !== 'fees' || counts.value.fees > 0),
    )
    // Visible rows, plus the active row so the focused cell and an open editor always exist.
    const windowRows: ComputedRef<FinancialReviewGridWindowRows> = computed(() => {
      const start = Math.max(0, Math.floor(scrollTop.value / rowHeight) - 8)
      const end = Math.min(shown.value.length, Math.ceil((scrollTop.value + viewport.value) / rowHeight) + 8)
      const items = shown.value.slice(start, end).map((row, offset) => ({ row, index: start + offset }))
      const row = shown.value[active.value.r]
      if (row && (active.value.r < start || active.value.r >= end)) items.push({ row, index: active.value.r })
      return items
    })
    const selection: ComputedRef<FinancialReviewGridSelection> = computed(() =>
      allSelected.value
        ? { top: 0, bottom: Math.max(0, shown.value.length - 1), left: 0, right: columns.length - 1 }
        : {
            top: Math.min(anchor.value.r, active.value.r),
            bottom: Math.max(anchor.value.r, active.value.r),
            left: Math.min(anchor.value.c, active.value.c),
            right: Math.max(anchor.value.c, active.value.c),
          },
    )
    const selectedCells = (): FinancialGridCell[] => {
      const cells: FinancialGridCell[] = []
      for (let r = selection.value.top; r <= selection.value.bottom; r += 1)
        for (let c = selection.value.left; c <= selection.value.right; c += 1) if (shown.value[r]) cells.push({ r, c })
      return cells
    }
    const inSelection = (r: number, c: number): boolean =>
      r >= selection.value.top && r <= selection.value.bottom && c >= selection.value.left && c <= selection.value.right
    const editableAt = (row: FinancialGridRow | undefined, c: number): boolean => !!row?.editable && !!columns[c].field
    const cellId = (r: number, c: number): string => `financial-grid-${r}-${c}`
    const numberLabel = (value: number): string => value.toLocaleString('en-GB')
    const amount = (value: string | null): string => (value === null ? '—' : financialAmountLabel(value))
    const roleNames: Record<string, string> = { base: 'Delivery', additional: 'Fee', credit: 'Credit' }

    // What a cell shows, and what it copies: amounts as plain decimals so they paste back as numbers.
    const display = (row: FinancialGridRow, key: FinancialGridColumnKey): string => {
      switch (key) {
        case 'shipment':
          return row.shipment
        case 'status':
          return row.reason ? `${row.status} · ${row.reason}` : row.status
        case 'charge':
          return `${row.charge} · ${roleNames[row.role] ?? row.role}`
        case 'cost':
          return amount(row.cost)
        case 'quotedPrice':
          return financialAmountLabel(row.quotedPrice, 'SEK')
        case 'price':
          return amount(row.price)
        case 'margin':
          return amount(row.margin)
        case 'marginPercent':
          return row.marginPercent ?? ''
        case 'basis':
          return row.basis
        case 'bill':
          return row.bill ? 'TRUE' : 'FALSE'
        case 'note':
          return row.note
        case 'booked':
          return row.booked
        case 'service':
          return row.service
        default:
          return row.grams === null ? '' : String(row.grams)
      }
    }
    const raw = (row: FinancialGridRow, key: FinancialGridColumnKey): string => {
      if (key === 'cost') return row.cost ?? ''
      if (key === 'quotedPrice') return row.quotedPrice ?? ''
      if (key === 'price') return row.price ?? ''
      if (key === 'margin') return row.margin ?? ''
      return display(row, key)
    }
    // Each cell names its column, so styles never depend on the column order.
    const cellClass = (r: number, c: number, row: FinancialGridRow): Record<string, boolean> => ({
      [`financial__grid-cell--${columns[c].key}`]: true,
      'financial__grid-cell--numeric': !!columns[c].numeric,
      'financial__grid-cell--field': editableAt(row, c),
      'financial__grid-cell--active': r === active.value.r && c === active.value.c,
      'financial__grid-cell--selected': inSelection(r, c),
      'financial__grid-cell--changed':
        (columns[c].key === 'price' && row.price !== null && row.pacsortPrice !== null && row.price !== row.pacsortPrice) ||
        (columns[c].key === 'bill' && !row.bill && row.editable) ||
        (columns[c].key === 'note' && row.editable && !!row.note),
      'financial__grid-cell--negative': columns[c].key === 'margin' && row.margin !== null && Number(row.margin) < 0,
    })
    const activeRow: ComputedRef<FinancialGridRow> = computed(() => shown.value[active.value.r])
    const activeRef: ComputedRef<string> = computed(() =>
      activeRow.value ? `${activeRow.value.shipment} · ${columns[active.value.c].label}` : 'No row selected',
    )
    const activeValue: ComputedRef<string> = computed(() => {
      const row = activeRow.value
      if (!row) return ''
      const column = columns[active.value.c]
      if (column.key === 'quotedPrice') return `${display(row, column.key)} · original shipment quote, repeated for context; do not sum fee rows`
      if (column.key === 'price' && row.editable)
        return `${amount(row.price)} · PacSort price ${amount(row.pacsortPrice)}${row.price !== row.pacsortPrice ? ' · changed in review' : ''}`
      if (column.key === 'bill') return row.bill ? 'Included in billing' : 'Left out: excluded with its note when you approve'
      return display(row, column.key) || (column.field && row.editable ? 'Empty · type to add' : '')
    })
    const selectionSummary: ComputedRef<string> = computed(() => {
      const cells = selectedCells()
      if (cells.length < 2) return ''
      const values = cells
        .filter((cell) => amountKeys.includes(columns[cell.c].key))
        .map((cell) => raw(shown.value[cell.r], columns[cell.c].key))
        .filter(Boolean)
      return values.length
        ? `${numberLabel(cells.length)} cells · Sum ${financialAmountLabel(sumFinancialAmounts(values), props.currency)}`
        : `${numberLabel(cells.length)} cells`
    })
    const saveText: ComputedRef<string> = computed(() =>
      props.save === 'saving'
        ? 'Saving…'
        : props.save === 'error'
          ? 'Not saved.'
          : props.save === 'saved'
            ? 'All changes saved'
            : 'Autosave on',
    )

    const focusGrid = (): void => {
      scroller.value?.focus({ preventScroll: true })
    }
    const ensureVisible = (): void => {
      const element = scroller.value
      // A hidden grid has no size yet; scrolling it then would leave the active cell out of view.
      if (!element || !element.clientWidth) return
      const top = (active.value.r + 1) * rowHeight
      if (top < element.scrollTop + rowHeight) element.scrollTop = top - rowHeight
      else if (top + rowHeight > element.scrollTop + element.clientHeight) element.scrollTop = top + rowHeight - element.clientHeight
      const left = columns.slice(0, active.value.c).reduce((total, column) => total + column.width, 0)
      const right = left + columns[active.value.c].width
      const frozen = columns[0].width
      if (active.value.c > 0 && left < element.scrollLeft + frozen) element.scrollLeft = left - frozen
      else if (right > element.scrollLeft + element.clientWidth) element.scrollLeft = right - element.clientWidth
    }
    const moveTo = (r: number, c: number, extend = false): void => {
      const last = Math.max(0, shown.value.length - 1)
      allSelected.value = false
      active.value = { r: Math.min(Math.max(r, 0), last), c: Math.min(Math.max(c, 0), columns.length - 1) }
      activeKey.value = shown.value[active.value.r]?.key ?? ''
      if (!extend) {
        anchor.value = { ...active.value }
        anchorKey.value = activeKey.value
      }
      nextTick(ensureVisible)
    }
    // Rows come and go as their status changes: find the active and anchor rows again by key.
    watch(shown, (rows) => {
      const last = Math.max(0, rows.length - 1)
      const find = (key: string, fallback: number): number => {
        const index = key ? rows.findIndex((row) => row.key === key) : -1
        return index >= 0 ? index : Math.min(fallback, last)
      }
      active.value = { ...active.value, r: find(activeKey.value, active.value.r) }
      anchor.value = { ...anchor.value, r: find(anchorKey.value, anchor.value.r) }
      if (!rows.some((row) => row.key === activeKey.value)) activeKey.value = rows[active.value.r]?.key ?? ''
      if (!rows.some((row) => row.key === anchorKey.value)) anchorKey.value = rows[anchor.value.r]?.key ?? ''
    })
    const setFilter = (value: FinancialGridFilter): void => {
      emit('update:filter', value)
    }
    const openShipment = (itemId: string): void => {
      emit('open-shipment', itemId)
    }
    watch([() => props.filter, search], () => {
      cancelEdit()
      activeKey.value = ''
      nextTick(() => {
        moveTo(0, active.value.c)
        if (scroller.value) scroller.value.scrollTop = 0
        measure()
      })
    })

    const emitEdits = (edits: FinancialGridEdit[], message: string): void => {
      if (!edits.length) return
      emit('edit', edits)
      announcement.value = message
    }
    // A pasted or typed price may use a decimal comma, spaces or a currency, but never a thousands separator or more
    // than öre: "1,234", "1.234" and "1.234,50" could each mean two different amounts, so they are refused.
    const priceText = (value: string): string | null => {
      const cleaned = value.replace(/[\s\u00a0]|sek|kr/gi, '')
      if (!cleaned) return ''
      if (cleaned.includes(',') && cleaned.includes('.')) return null
      if (/^-?\d{1,3}([,.]\d{3})+$/.test(cleaned)) return null
      const decimal = financialDecimal(cleaned)
      if (decimal === null || (decimal.split('.')[1] ?? '').length > 2) return null
      return decimal
    }
    // Spreadsheet booleans, in English and Swedish; anything else is not a Bill value.
    const boolText = (value: string): boolean | null => {
      const text = value.trim()
      if (/^(true|sant|ja|yes|y|x|1|✓)$/i.test(text)) return true
      if (/^(false|falskt|nej|no|n|0)$/i.test(text)) return false
      return null
    }
    const editFor = (row: FinancialGridRow | undefined, field: FinancialGridField, value: string): FinancialGridEdit | null => {
      if (!row?.editable) return null
      if (field === 'price') {
        const price = priceText(value)
        return price === null ? null : { key: row.key, field, value: price }
      }
      if (field === 'bill') {
        const bill = boolText(value)
        return bill === null ? null : { key: row.key, field, value: bill }
      }
      return { key: row.key, field, value }
    }

    const editorElement = (): HTMLInputElement | null => (Array.isArray(editor.value) ? editor.value[0] : editor.value) ?? null
    const toggleBill = (): void => {
      const row = activeRow.value
      if (!row) return
      const value = !row.bill
      const edits = selectedCells()
        .filter((cell) => columns[cell.c].key === 'bill')
        .map((cell) => shown.value[cell.r])
        .filter((item) => item?.editable)
        .map((item) => ({ key: item.key, field: 'bill' as const, value }))
      emitEdits(
        edits.length ? edits : row.editable ? [{ key: row.key, field: 'bill', value }] : [],
        value ? 'Marked to bill.' : 'Marked not to bill. Add a note with the reason.',
      )
    }
    const startEdit = (mode: 'enter' | 'edit', initial?: string): void => {
      if (editing.value) return
      const row = activeRow.value
      const column = columns[active.value.c]
      if (!row || !column.field || !row.editable) {
        if (row && !row.editable && column.field) announcement.value = `${row.status}: this row cannot be changed.`
        return
      }
      if (column.field === 'bill') {
        toggleBill()
        return
      }
      editMode.value = mode
      editError.value = ''
      editKey.value = row.key
      editColumn.value = active.value.c
      openedWith.value = column.field === 'price' ? (row.price ?? '') : row.note
      draft.value = initial ?? openedWith.value
      editing.value = true
      nextTick(() => {
        const input = editorElement()
        input?.focus()
        if (input && initial === undefined) input.setSelectionRange(input.value.length, input.value.length)
      })
    }
    const cancelEdit = (): void => {
      editing.value = false
      editError.value = ''
    }
    // Saves the open edit. An unchanged value is not an edit: it neither changes the price nor settles a check.
    const commit = (): boolean => {
      if (!editing.value) return true
      const row = props.rows.find((item) => item.key === editKey.value)
      const column = columns[editColumn.value]
      if (!row || !column.field) {
        cancelEdit()
        return true
      }
      const before = priceText(openedWith.value)
      const after = priceText(draft.value)
      const unchanged =
        column.field === 'price'
          ? before !== null && after !== null && (before === after || (!!before && !!after && financialAmountsEqual(before, after, 2)))
          : draft.value === openedWith.value
      if (unchanged) {
        cancelEdit()
        return true
      }
      const edit = editFor(row, column.field, draft.value)
      if (!edit) {
        editError.value =
          'Enter the customer price as a number in öre or kronor, for example 27.63 or 27,63, without thousands separators. Leave it empty for the PacSort price.'
        announcement.value = editError.value
        return false
      }
      emitEdits([edit], `${column.label} for ${row.shipment} changed.`)
      cancelEdit()
      return true
    }
    // Leaves an edit that cannot be saved without trapping the user: the value is dropped and the grid says so.
    const abandon = (): void => {
      if (!editing.value) return
      cancelEdit()
      announcement.value = 'Not saved: the price was not a valid amount.'
    }
    const onEditorKey = (event: KeyboardEvent): void => {
      const moves: Record<string, [number, number]> = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }
      if (event.key === 'Escape') {
        event.preventDefault()
        cancelEdit()
        nextTick(focusGrid)
      } else if (event.key === 'Enter' || event.key === 'Tab' || (editMode.value === 'enter' && moves[event.key])) {
        const [dr, dc] =
          event.key === 'Enter' ? [event.shiftKey ? -1 : 1, 0] : event.key === 'Tab' ? [0, event.shiftKey ? -1 : 1] : moves[event.key]
        const leaves = event.key === 'Tab' && (active.value.c + dc < 0 || active.value.c + dc >= columns.length)
        if (!leaves) event.preventDefault()
        if (!commit()) {
          if (leaves) event.preventDefault()
          return
        }
        if (leaves) return
        moveTo(active.value.r + dr, active.value.c + dc)
        nextTick(focusGrid)
      }
      event.stopPropagation()
    }
    const onEditorBlur = (): void => {
      if (editing.value && !commit()) abandon()
    }
    const clearSelection = (): void => {
      const edits: FinancialGridEdit[] = []
      for (const cell of selectedCells()) {
        const row = shown.value[cell.r]
        const field = columns[cell.c].field
        if (!row?.editable || !field) continue
        edits.push({ key: row.key, field, value: field === 'bill' ? true : '' })
      }
      emitEdits(edits, `${edits.length} ${edits.length === 1 ? 'cell' : 'cells'} reset to the PacSort price, billed and without a note.`)
    }
    // Ctrl/Cmd+D: copies the top row of the selection down, or the row above into a single selected row.
    const fillDown = (): void => {
      const single = selection.value.top === selection.value.bottom
      const sourceRow = shown.value[single ? selection.value.top - 1 : selection.value.top]
      if (!sourceRow) return
      const first = single ? selection.value.top : selection.value.top + 1
      const edits: FinancialGridEdit[] = []
      for (let c = selection.value.left; c <= selection.value.right; c += 1) {
        const field = columns[c].field
        if (!field) continue
        const value = field === 'bill' ? String(sourceRow.bill) : field === 'price' ? (sourceRow.price ?? '') : sourceRow.note
        for (let r = first; r <= selection.value.bottom; r += 1) {
          const edit = editFor(shown.value[r], field, value)
          if (edit) edits.push(edit)
        }
      }
      emitEdits(edits, `Filled ${edits.length} ${edits.length === 1 ? 'cell' : 'cells'} down.`)
    }

    const onKey = (event: KeyboardEvent): void => {
      if (editing.value || !shown.value.length) return
      const mod = event.ctrlKey || event.metaKey
      const key = event.key
      const { r, c } = active.value
      const page = Math.max(1, Math.floor(viewport.value / rowHeight) - 1)
      const moves: Record<string, [number, number]> = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }
      if (moves[key]) {
        event.preventDefault()
        const [dr, dc] = moves[key]
        const target = mod
          ? { r: dr ? (dr > 0 ? shown.value.length - 1 : 0) : r, c: dc ? (dc > 0 ? columns.length - 1 : 0) : c }
          : { r: r + dr, c: c + dc }
        moveTo(target.r, target.c, event.shiftKey)
        return
      }
      if (mod && key.toLowerCase() === 'a') {
        event.preventDefault()
        allSelected.value = true
        announcement.value = `All ${shown.value.length} rows selected.`
        return
      }
      if (mod && key.toLowerCase() === 'd') {
        event.preventDefault()
        fillDown()
        return
      }
      if (mod && (key.toLowerCase() === 'y' || (key.toLowerCase() === 'z' && event.shiftKey))) {
        event.preventDefault()
        redo()
        return
      }
      if (mod && key.toLowerCase() === 'z') {
        event.preventDefault()
        undo()
        return
      }
      if (mod && key === 'Enter') {
        event.preventDefault()
        const row = activeRow.value
        if (row?.itemId) openShipment(row.itemId)
        return
      }
      if (mod) return
      switch (key) {
        case 'Tab': {
          const next = c + (event.shiftKey ? -1 : 1)
          // At the first or last column, Tab leaves the grid like any other control.
          if (next < 0 || next >= columns.length) return
          event.preventDefault()
          moveTo(r, next)
          return
        }
        case 'Enter':
          event.preventDefault()
          if (columns[c].field && activeRow.value?.editable) startEdit('edit')
          else if (columns[c].key === 'shipment' && activeRow.value?.itemId) openShipment(activeRow.value.itemId)
          else moveTo(r + (event.shiftKey ? -1 : 1), c)
          return
        case 'F2':
          event.preventDefault()
          startEdit('edit')
          return
        case ' ':
          event.preventDefault()
          if (columns[c].field === 'bill') toggleBill()
          else if (columns[c].field === 'note' && activeRow.value?.editable) startEdit('enter', ' ')
          return
        case 'Delete':
        case 'Backspace':
          event.preventDefault()
          clearSelection()
          return
        case 'Escape':
          allSelected.value = false
          anchor.value = { ...active.value }
          anchorKey.value = activeKey.value
          return
        case 'Home':
          event.preventDefault()
          moveTo(r, 0, event.shiftKey)
          return
        case 'End':
          event.preventDefault()
          moveTo(r, columns.length - 1, event.shiftKey)
          return
        case 'PageDown':
          event.preventDefault()
          moveTo(r + page, c, event.shiftKey)
          return
        case 'PageUp':
          event.preventDefault()
          moveTo(r - page, c, event.shiftKey)
          return
        default:
          if (key.length === 1 && !event.altKey && columns[c].field && columns[c].field !== 'bill') {
            event.preventDefault()
            startEdit('enter', key)
          }
      }
    }

    const onCopy = (event: ClipboardEvent): void => {
      if (editing.value || !shown.value.length) return
      const lines: string[] = []
      for (let r = selection.value.top; r <= selection.value.bottom; r += 1) {
        const row = shown.value[r]
        if (!row) continue
        const cells: string[] = []
        for (let c = selection.value.left; c <= selection.value.right; c += 1)
          cells.push(raw(row, columns[c].key).replace(/[\t\n]/g, ' '))
        lines.push(cells.join('\t'))
      }
      event.clipboardData?.setData('text/plain', lines.join('\n'))
      event.preventDefault()
      announcement.value = `Copied ${selectedCells().length} cells.`
    }
    // Tab-separated text as Google Sheets and Excel write it: a quoted cell may hold tabs, line breaks and "".
    const parseCells = (text: string): string[][] => {
      const rows: string[][] = [[]]
      let cell = ''
      let quoted = false
      const source = text.replace(/\r/g, '')
      for (let index = 0; index < source.length; index += 1) {
        const character = source[index]
        if (quoted) {
          if (character === '"' && source[index + 1] === '"') {
            cell += '"'
            index += 1
          } else if (character === '"') quoted = false
          else cell += character
        } else if (character === '"' && cell === '') quoted = true
        else if (character === '\t') {
          rows[rows.length - 1].push(cell)
          cell = ''
        } else if (character === '\n') {
          rows[rows.length - 1].push(cell)
          rows.push([])
          cell = ''
        } else cell += character
      }
      rows[rows.length - 1].push(cell)
      if (rows.length > 1 && rows[rows.length - 1].length === 1 && rows[rows.length - 1][0] === '') rows.pop()
      return rows
    }
    // Pastes cells from the top-left of the selection; one value fills the whole selection.
    const onPaste = (event: ClipboardEvent): void => {
      if (editing.value || !shown.value.length) return
      const text = event.clipboardData?.getData('text/plain') ?? ''
      if (!text) return
      event.preventDefault()
      const matrix = parseCells(text)
      const edits: FinancialGridEdit[] = []
      let skipped = 0
      const place = (r: number, c: number, value: string): void => {
        const field = columns[c]?.field
        const edit = field ? editFor(shown.value[r], field, value) : null
        if (edit) edits.push(edit)
        else skipped += 1
      }
      if (matrix.length === 1 && matrix[0].length === 1) for (const cell of selectedCells()) place(cell.r, cell.c, matrix[0][0])
      else
        matrix.forEach((cells, i) =>
          cells.forEach((value, j) => {
            if (selection.value.top + i < shown.value.length) place(selection.value.top + i, selection.value.left + j, value)
          }),
        )
      emitEdits(
        edits,
        `Pasted ${edits.length} ${edits.length === 1 ? 'cell' : 'cells'}.${skipped ? ` ${skipped} skipped: read-only or not a valid value.` : ''}`,
      )
      if (!edits.length) announcement.value = 'Nothing pasted: those cells are read-only or the values are not valid.'
    }

    const onDown = (r: number, c: number, event: MouseEvent): void => {
      if (event.button !== 0) return
      if (editing.value && !commit()) abandon()
      event.preventDefault()
      focusGrid()
      if (event.shiftKey) moveTo(r, c, true)
      else {
        moveTo(r, c)
        dragging = true
      }
    }
    // A single click on an editable Bill cell ticks it; a drag that starts there or a double-click does not.
    const onClick = (r: number, c: number, event: MouseEvent): void => {
      if (event.detail !== 1 || event.shiftKey || columns[c].field !== 'bill' || !shown.value[r]?.editable) return
      if (active.value.r === r && active.value.c === c && anchor.value.r === r && anchor.value.c === c) toggleBill()
    }
    const onDouble = (c: number): void => {
      if (!editing.value && columns[c].field !== 'bill') startEdit('edit')
    }
    const onEnter = (r: number, c: number): void => {
      if (dragging) moveTo(r, c, true)
    }
    const stopDrag = (): void => {
      dragging = false
    }
    const onScroll = (): void => {
      scrollTop.value = scroller.value?.scrollTop ?? 0
    }
    const measure = (): void => {
      viewport.value = scroller.value?.clientHeight || 480
    }
    const undo = (): void => {
      if (!props.canUndo) return
      emit('undo')
      announcement.value = 'Undone.'
    }
    const redo = (): void => {
      if (!props.canRedo) return
      emit('redo')
      announcement.value = 'Redone.'
    }
    // The button goes as soon as the save restarts, so focus returns to the grid instead of the page.
    const retry = (): void => {
      emit('retry')
      focusGrid()
    }
    // Esc closes the Shortcuts disclosure, and so does moving on, so it never stays open over the grid.
    const closeHelp = (): void => {
      if (!help.value?.open) return
      help.value.open = false
      help.value.querySelector('summary')?.focus()
    }
    const leaveHelp = (event: FocusEvent): void => {
      if (help.value && !help.value.contains(event.relatedTarget as Node | null)) help.value.open = false
    }
    onMounted(() => {
      measure()
      moveTo(0, active.value.c)
      window.addEventListener('mouseup', stopDrag)
      if (typeof ResizeObserver !== 'undefined' && scroller.value) {
        resize = new ResizeObserver(measure)
        resize.observe(scroller.value)
      } else window.addEventListener('resize', measure)
    })
    onBeforeUnmount(() => {
      window.removeEventListener('mouseup', stopDrag)
      window.removeEventListener('resize', measure)
      resize?.disconnect()
    })
    return {
      rowHeight,
      columns,
      template,
      totalWidth,
      filterOptions,
      shortcuts,
      scroller,
      editor,
      help,
      search,
      active,
      editing,
      editKey,
      editColumn,
      draft,
      editError,
      announcement,
      shown,
      counts,
      windowRows,
      inSelection,
      editableAt,
      cellId,
      numberLabel,
      display,
      cellClass,
      activeRef,
      activeValue,
      selectionSummary,
      saveText,
      setFilter,
      onEditorKey,
      onEditorBlur,
      onKey,
      onCopy,
      onPaste,
      onDown,
      onClick,
      onDouble,
      onEnter,
      onScroll,
      undo,
      redo,
      retry,
      openShipment,
      closeHelp,
      leaveHelp,
    }
  },
})
</script>
